<template>
  <el-dialog
    title="Create Pickup"
    width="97vw"
    top="2vh"
    :visible.sync="isVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="closable"
    @closed="$emit('destroy')"
  >
    <el-dialog
      width="30%"
      title="Notice"
      :visible="isPrompting && isVisible"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      append-to-body
    >
      The booking reference of the Pickup is <b>{{ pickup.booking_reference }}</b>.

      <div slot="footer">
        <el-button
          size="small"
          :disabled="!closable"
          @click.native="processPickup"
        >
          Process
        </el-button>

        <el-button
          size="small"
          :disabled="!closable"
          @click.native="promptPickupConfirmationReference"
        >
          Confirm
        </el-button>

        <el-button
          size="small"
          :disabled="!closable"
          @click.native="isVisible = false"
        >
          Hold
        </el-button>
      </div>
    </el-dialog>

    <PickupCreateForm
      v-loading="isProcessing"
      :pickup="pickup"
    />

    <template #footer>
      <el-button
        :disabled="!closable"
        @click.native="isVisible = false"
      >
        Cancel
      </el-button>

      <el-button
        type="primary"
        :disabled="!closable"
        @click.native="createPickup"
      >
        Create
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import PickupCreateForm from '@/components/Application/CustomerService/Pickups/Pickup/CreateForm'
import Pickup from '@/plugins/sdk/resources/Pickup'

export default {
  components: {
    PickupCreateForm
  },
  props: {
    pickup: {
      type: Object,
      required: true,
      validator (value) {
        return value instanceof Pickup
      }
    },
    previousPickup: {
      type: Object,
      default: () => null,
      validator (value) {
        return value instanceof Pickup
      }
    }
  },
  data () {
    return {
      reservationNotificationInstance: undefined,
      isVisible: true,
      isProcessing: false,
      isPrompting: false,
      isReserving: false
    }
  },
  computed: {
    closable () {
      return !this.isProcessing
    },
    reservable () {
      return this.pickup.account &&
        this.pickup.team &&
        this.pickup.carrier &&
        this.pickup.carrier_service
    },
    reserved () {
      return !this.isReserving && this.pickup.exists
    }
  },
  watch: {
    reservable (value) {
      if (value && !this.reserved) {
        this.reservePickup()
      }
    }
  },
  beforeDestroy () {
    if (typeof this.reservationNotificationInstance !== 'undefined') {
      this.reservationNotificationInstance.close()
    }
  },
  methods: {
    async reservePickup () {
      if (!this.reserved) {
        this.isReserving = true

        try {
          const pickup = await this
            .$sdk
            .pickup
            .create({
              type: 'reserved'
            })

          this.pickup.fill({
            id: pickup.id,
            booking_reference: pickup.booking_reference
          }, true)

          this.reservationNotificationInstance = this
            .$notify
            .success({
              title: 'Pickup Reserved',
              message: `The Booking Reference is ${this.pickup.booking_reference}`,
              duration: 0
            })
        } catch {
          this.$message.warning('Failed to reserve a Booking Reference')
        } finally {
          this.isReserving = false
        }
      }
    },
    async createPickup () {
      this.isProcessing = true

      this.pickup.type = 'adhoc'
      this.pickup.status = 'held'

      try {
        await this.pickup.save()

        if (this.previousPickup) {
          // update the previous pickup status to re-booked
          await this.previousPickup.update(
            request => {
              request.body = { status: 're-booked' }
            }
          )
          await this
            .$sdk
            .note
            .create({
              content: `New booking reference: ${this.pickup.booking_reference}`,
              resource: 'pickup',
              resource_uuid: this.previousPickup.id
            })
        }

        this.isPrompting = true
      } finally {
        this.isProcessing = false
      }
    },
    async processPickup () {
      this.isProcessing = true

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              const pickup = this.pickup.clone()
              pickup.status = 'pending'
              request.body = pickup
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
        this.isVisible = false
      } finally {
        this.isProcessing = false
      }
    },
    async confirmPickup (reference) {
      this.isPrompting = false
      this.isProcessing = true

      try {
        this.pickup = await this
          .pickup
          .update(
            request => {
              request.body = { carrier_reference: reference, status: 'confirmed' }
            }
          )

        this.$message.success(`Updated Pickup ${this.pickup.booking_reference}`)
        this.isVisible = false
      } finally {
        this.isProcessing = false
      }
    },
    promptPickupConfirmationReference () {
      this
        .$prompt(
          'Enter a Confirmation Reference',
          `Confirm Pickup ${this.pickup.booking_reference}`,
          {
            inputValidator: value => {
              return value && value.length > 0 ? true : 'A confirmation reference is required'
            },
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            callback: (action, instance) => {
              if (action === 'confirm') {
                this.isPrompting = false
                this.confirmPickup(instance.inputValue)
              }
            }
          }
        )
    }
  }
}
</script>
